import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';

export default function Navbar() {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPartnerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Increased height from h-16 to h-20 */}
        <div className="flex justify-between h-20 items-center">
          {/* Logo - Made bigger */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696ab3312ed1fbc29124b9f0.png" 
                alt="SpeechFormation" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Larger fonts */}
          <div className="hidden md:flex items-center space-x-2">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                >
                  Dashboard
                </Link>
                
                <Link 
                  to="/games" 
                  className="text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                >
                  Games
                </Link>
                
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                  >
                    Admin
                  </Link>
                )}

                <div className="flex items-center space-x-4 ml-6 pl-6 border-l border-gray-200">
                  <span className="text-base text-gray-600 font-medium">
                    Hi, {user?.name?.split(' ')[0]}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-red-600 px-4 py-2 font-semibold text-base transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/games" 
                  className="text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                >
                  Games
                </Link>

                {/* Partner With Us Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setPartnerDropdownOpen(!partnerDropdownOpen)}
                    className="flex items-center text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                  >
                    Partner With Us
                    <svg 
                      className={`ml-1.5 w-4 h-4 transition-transform ${partnerDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {partnerDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                      <Link
                        to="/for-parents"
                        className="flex items-center px-5 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setPartnerDropdownOpen(false)}
                      >
                        <span className="text-2xl mr-4">👨‍👩‍👧</span>
                        <div>
                          <div className="font-semibold text-base">For Parents</div>
                          <div className="text-sm text-gray-500">Help at home</div>
                        </div>
                      </Link>
                      <Link
                        to="/for-therapists"
                        className="flex items-center px-5 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setPartnerDropdownOpen(false)}
                      >
                        <span className="text-2xl mr-4">🩺</span>
                        <div>
                          <div className="font-semibold text-base">For Therapists</div>
                          <div className="text-sm text-gray-500">Join our network</div>
                        </div>
                      </Link>
                      <Link
                        to="/for-schools"
                        className="flex items-center px-5 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setPartnerDropdownOpen(false)}
                      >
                        <span className="text-2xl mr-4">🏫</span>
                        <div>
                          <div className="font-semibold text-base">For Schools</div>
                          <div className="text-sm text-gray-500">District programs</div>
                        </div>
                      </Link>
                      <Link
                        to="/for-creatives"
                        className="flex items-center px-5 py-3.5 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setPartnerDropdownOpen(false)}
                      >
                        <span className="text-2xl mr-4">🎨</span>
                        <div>
                          <div className="font-semibold text-base">For Creatives</div>
                          <div className="text-sm text-gray-500">Earn with your art</div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                <Link 
                  to="/pricing" 
                  className="text-gray-700 hover:text-primary-600 hover:bg-primary-50 px-4 py-2.5 rounded-lg font-semibold text-base transition-colors"
                >
                  Pricing
                </Link>

                <div className="flex items-center space-x-3 ml-6 pl-6 border-l border-gray-200">
                  <Link 
                    to="/login" 
                    className="text-gray-700 hover:text-primary-600 px-4 py-2.5 font-semibold text-base transition-colors"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-2.5 rounded-full font-bold text-base transition-all shadow-md hover:shadow-lg"
                  >
                    Get Started Free
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-600 hover:text-primary-600 p-2 rounded-lg"
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/games" 
                  className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Games
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                <div className="border-t border-gray-100 my-2"></div>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-semibold text-base"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/games" 
                  className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Games
                </Link>
                
                {/* Mobile Partner Links */}
                <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Partner With Us
                </div>
                <Link 
                  to="/for-parents" 
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mr-3 text-xl">👨‍👩‍👧</span> For Parents
                </Link>
                <Link 
                  to="/for-therapists" 
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mr-3 text-xl">🩺</span> For Therapists
                </Link>
                <Link 
                  to="/for-schools" 
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mr-3 text-xl">🏫</span> For Schools
                </Link>
                <Link 
                  to="/for-creatives" 
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mr-3 text-xl">🎨</span> For Creatives
                </Link>
                
                <div className="border-t border-gray-100 my-2"></div>
                
                <Link 
                  to="/pricing" 
                  className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link 
                  to="/login" 
                  className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg font-semibold text-base"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="block px-4 py-3 bg-accent-500 text-white text-center rounded-full font-bold text-base mt-3 shadow-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
